import type { IconProps } from '@amzn/awsui-components-console';
import type { DiscoverySource, InputSource, RuleInput } from '@amzn/helianthus-arbiter-client';

export type SourceType = 'discovery' | 'arbiter' | 'call' | 'rule';

/** One row per InputSource member: display label plus the single value field the form edits for it. */
export const SOURCE_TYPES: Record<SourceType, { label: string; icon: IconProps.Name; field: string; placeholder: string; description?: string; required?: boolean }> = {
  discovery: { label: 'Discovery', icon: 'search', field: 'Filter', placeholder: 'Optional YAQL filter expression', description: 'Optional YAQL expression applied to the discovered resource' },
  arbiter: { label: 'Arbiter', icon: 'settings', field: 'Key', placeholder: '$raw_diff', description: 'Arbiter runtime value, e.g. $raw_diff, $resource_id, $resource_type', required: true },
  call: { label: 'Call', icon: 'call', field: 'Key', placeholder: 'call.key', required: true },
  rule: { label: 'Rule', icon: 'internal-link', field: 'Target Rule', placeholder: 'extract_disco___sigv4Enabled_6b31ec55', description: 'Name of the rule whose result feeds this input', required: true },
};

export const SOURCE_TYPE_OPTIONS = (Object.keys(SOURCE_TYPES) as SourceType[]).map((value) => ({
  label: SOURCE_TYPES[value].label,
  iconName: SOURCE_TYPES[value].icon,
  value,
}));

export function sourceTypeOf(source: InputSource | undefined): SourceType | undefined {
  return (Object.keys(SOURCE_TYPES) as SourceType[]).find((key) => source?.[key]);
}

export interface InputFormState {
  name: string;
  type: string;
  sourceType: SourceType;
  /** The source-specific value: filter expression, key, or target rule name. */
  value: string;
  /** Original discovery source so fields the form does not edit survive a round-trip. */
  original?: DiscoverySource;
}

export function fromRuleInput(input: RuleInput): InputFormState {
  const s = input.source;
  return {
    name: input.name ?? '',
    type: input.type ?? '',
    sourceType: sourceTypeOf(s) ?? 'discovery',
    value: s?.arbiter?.key ?? s?.call?.key ?? s?.rule?.target ?? s?.discovery?.filter?.expression ?? '',
    original: s?.discovery,
  };
}

export function toRuleInputs(inputs: InputFormState[]): RuleInput[] {
  return inputs.map(({ name, type, sourceType, value, original }) => {
    const source: InputSource =
      sourceType === 'discovery'
        ? {
          discovery: {
            discoveryType: 'DESCRIBE_RESOURCE',
            ...original,
            filter: value ? { language: original?.filter?.language ?? 'yaql', expression: value } : undefined,
          },
        }
        : sourceType === 'arbiter' ? { arbiter: { key: value } }
          : sourceType === 'call' ? { call: { key: value } }
            : { rule: { target: value } };
    return { name: name.trim(), source, type: type.trim() };
  });
}
